import { ColDef } from 'ag-grid-community';
import { ICoordinates } from 'types/map.types';
import { IGraphQlConnection } from 'types/core.types';
import {
  TColumnDefinition,
  TColDefWithValidator,
  TSystemColumns,
} from 'FeatureModules/DataImport/Components/DataImportTable/excelTable.types';
import { IBasePartnerCompany } from '../../Partner/types/partner.types';
import {
  TPlotGeometryDatasetColumn,
  TFarmSizeDatasetColumn,
  TPlotSizeDatasetColumn,
} from './dataset.types';

export enum EDataImportCategories {
  Farms = 'farms',
}

export type TFarm = {
  id: string;
  farmExternalId: string;
  siteTitle: string;
  rawMaterialIds: string[];
  locationCoordinates?: ICoordinates;
  farmSize?: number;
  ownedById?: string;
  farmDescription?: string;
};

export type TFirstMileImport = {
  id: string;
  title?: string;
  createdTimestamp: Date;
  createdBy: IBasePartnerCompany;
  modifiedTimestamp: Date;
  language: string;
};

export interface IFarmResponse extends TFirstMileImport {
  rows: IGraphQlConnection<TFarm>;
}

export interface IFirstMileImportsResponse {
  firstMileImports: IGraphQlConnection<TFirstMileImport>;
}

export interface IFirstMileImportResponse {
  firstMileImport: IFarmResponse;
}

export type TFarmColumns = {
  id: string;
  farmId: TColumnDefinition<string>;
  name: TColumnDefinition<string>;
  crop: TColumnDefinition<string>;
  location: TColumnDefinition<string>;
  farmSize: TColumnDefinition<number>;
  weight?: TColumnDefinition<number>;
  description?: TColumnDefinition<string>;
};

export type TDataImportColumn = TColDefWithValidator | ColDef<TSystemColumns>;

export type TCoordinateVersion = 'latlng' | 'lnglat';

export type TFarmSizeUnit = 'ha' | 'km2';

export type TWeightUnit = 'kg' | 't';

export interface ITableSettings {
  coordinatesVersion: TCoordinateVersion;
  farmSizeUnit: TFarmSizeUnit;
  weightUnit: TWeightUnit;
}

export type TOriginTableSettingsColumn<T = unknown> = {
  enabled: boolean;
  format?: T;
};

export interface IOriginTableSettings {
  overwriteExisting: boolean;
  clusterSites: boolean;
  columns: {
    farmId: TOriginTableSettingsColumn<string>;
    plotId: TOriginTableSettingsColumn<string>;
    plotName: TOriginTableSettingsColumn<string>;
    plotSize: TOriginTableSettingsColumn<TPlotSizeDatasetColumn>;
    geometry: TOriginTableSettingsColumn<TPlotGeometryDatasetColumn>;
    originClusterId: TOriginTableSettingsColumn<string>;
    farmSize: TOriginTableSettingsColumn<TFarmSizeDatasetColumn>;
    farmName: TOriginTableSettingsColumn<string>;
    farmOwnerFullName: TOriginTableSettingsColumn<string>;
    farmOwnerFirstName: TOriginTableSettingsColumn<string>;
    farmOwnerLastName: TOriginTableSettingsColumn<string>;
    landOwnership: TOriginTableSettingsColumn<string>;
    numberOfFarmers: TOriginTableSettingsColumn<number>;
    additionalCrops: TOriginTableSettingsColumn<string>;
    siteDescription: TOriginTableSettingsColumn<string>;
  };
}

export type TColumnKeys = keyof IOriginTableSettings['columns'];

export type TValidationResult = {
  isValid?: boolean;
  validationMessage?: string;
};

export enum TableCellStatus {
  VALID = 'VALID',
  ERROR = 'ERROR',
  WARN = 'WARN',
  EMPTY = 'EMPTY',
}

export type TableCellStatusType = `${TableCellStatus}`;
